import { ChangeEvent, FC, useEffect, useMemo, useState } from "react";
import {
	EnableDisableModel,
	ProductEnableDisableMessage,
	ProductEnableDisableModel,
} from "Infrastructure/Models/Messages/Messages";
import {
	CategoryDeviceModel,
	getCategoryDisplayName,
	getProductTypeDisplayName,
	ProductDeviceModel,
	ProductStatusDeviceModel,
	ProductTypeDeviceModel,
	TariffDeviceModel,
} from "Infrastructure/Utils/Nsure/ProductUtils";
import {
	Button,
	Checkbox,
	FormControl,
	InputLabel,
	MenuItem,
	Select,
	SelectChangeEvent,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Box,
	Typography,
	Stack,
	TableRowProps,
	Divider,
	TextField,
	Tooltip,
	IconButton,
} from "@mui/material";
import { v4 as uuidv4 } from "uuid";
import { DeviceForm } from "Infrastructure/Utils/Nsure/DeviceUtils";
import FactCheckCircleIcon from "@mui/icons-material/FactCheck";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import CancelIcon from "@mui/icons-material/Cancel";
import HistoryEduIcon from "@mui/icons-material/HistoryEdu";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckBoxSharpIcon from "@mui/icons-material/CheckBoxSharp";
import DisabledByDefaultIcon from "@mui/icons-material/DisabledByDefault";
import styled from "@emotion/styled";
import { BlConfirmButton } from "Shared/Buttons/BlConfirmButton";
import { useSnackbar } from "Infrastructure/Providers/SnackbarProvider/SnackbarProvider";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import useDebounce from "Infrastructure/Hooks/useDebounce";
import { BlTooltip } from "Shared/BlTooltip";
import { Category } from "Infrastructure/Models/Messages/Dto/ProductStatus";
import { CategoryFeatureTable } from "./CategoryFeatureTable";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SaveIcon from '@mui/icons-material/Save';

interface ProductStatusSelectionModel {
	categories: number[];
	productTypes: number[];
	products: number[];
	tariffs: number[];
}

type Props = {
	deviceForm: DeviceForm;
	productDeviceModel: ProductStatusDeviceModel | null;
	hubConnection: any;
	closeDialog: any;
	onEnableDisable: (message: ProductEnableDisableMessage) => void;
	selectionModel: ProductStatusSelectionModel;
	setSelectionModel: (model: ProductStatusSelectionModel) => void;
	productEnableDisableMessage: ProductEnableDisableMessage;
	setProductEnableDisableMessage: (
		message: ProductEnableDisableMessage
	) => void;
	isHistoryLoaded?: boolean;
};

const ProductListTable: FC<Props> = ({
	deviceForm,
	productDeviceModel,
	hubConnection,
	closeDialog,
	onEnableDisable,
	selectionModel,
	setSelectionModel,
	productEnableDisableMessage,
	setProductEnableDisableMessage,
	isHistoryLoaded = false,
}) => {
	const showSnackbar = useSnackbar();
	const { t } = useResource();

	const [selectedCategoryID, setSelectedCategoryID] = useState<number | null>(
		null
	);
	const [selectedProductTypeID, setSelectedProductTypeID] = useState<
		number | null
	>(null);
	const [selectedProductID, setSelectedProductID] = useState<number | null>(
		null
	);

	const [selectedInstitutionID, setSelectedInstitutionID] = useState<
		number | null
	>(null);

	// Expanded rows
	const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

	// Table visibility states
	const [isProductTypeTableVisible, setIsProductTypeTableVisible] =
		useState(false);
	const [isProductTableVisible, setIsProductTableVisible] = useState(false);
	const [isTariffTableVisible, setIsTariffTableVisible] = useState(false);

	const handleSelectionChange = (
		id: number,
		type: keyof ProductStatusSelectionModel
	) => {
		const newSelection = { ...selectionModel };
		const index = newSelection[type].indexOf(id);
		if (index > -1) {
			newSelection[type].splice(index, 1);
		} else {
			newSelection[type].push(id);
		}
		setSelectionModel(newSelection);
	};

	const handleSelectAll = (
		type: keyof ProductStatusSelectionModel,
		items: number[]
	) => {
		const allSelected = items.every((id) => selectionModel[type].includes(id));
		const newSelection = allSelected
			? selectionModel[type].filter((id) => !items.includes(id)) // Deselect all if already selected
			: Array.from(new Set([...selectionModel[type], ...items])); // Select all if not already selected

		setSelectionModel({ ...selectionModel, [type]: newSelection });
	};

	const handleEnableDisable = (doEnable: boolean) => {
		const updateMessage = (
			currentArray: EnableDisableModel[],
			selection: number[]
		): EnableDisableModel[] => {
			const newArray = [...currentArray];
			for (const id of selection) {
				const index = newArray.findIndex((item) => item.id === id);
				if (index > -1) {
					newArray[index].doEnable = doEnable; // Update if exists
				} else {
					newArray.push({ id, doEnable }); // Add new if not exists
				}
			}
			return newArray;
		};

		setProductEnableDisableMessage({
			...productEnableDisableMessage,
			requestId: uuidv4(),
			reason: reasonText,
			filteredDeviceNames: deviceForm.devices
				.filter((x) => x.isChecked)
				.map((x) => x.name),
			categories: updateMessage(
				productEnableDisableMessage.categories || [],
				selectionModel.categories
			),
			productTypes: updateMessage(
				productEnableDisableMessage.productTypes || [],
				selectionModel.productTypes
			),
			products: updateMessage(
				productEnableDisableMessage.products || [],
				selectionModel.products
			),
			tariffs: updateMessage(
				productEnableDisableMessage.tariffs || [],
				selectionModel.tariffs
			),
		});
	};

	const handleEnableDisableContractCreate = (
		doEnableContractCreate: boolean
	) => {
		const updateMessage = (
			currentArray: ProductEnableDisableModel[],
			selection: number[]
		): ProductEnableDisableModel[] => {
			const newArray = [...currentArray];
			for (const id of selection) {
				const index = newArray.findIndex((item) => item.id === id);
				if (index > -1) {
					newArray[index].doEnableContractCreate = doEnableContractCreate; // Update if exists
				} else {
					newArray.push({ id, doEnableContractCreate }); // Add new if not exists
				}
			}
			return newArray;
		};

		setProductEnableDisableMessage({
			...productEnableDisableMessage,
			requestId: uuidv4(),
			reason: reasonText,
			filteredDeviceNames: deviceForm.devices
				.filter((x) => x.isChecked)
				.map((x) => x.name),
			products: updateMessage(
				productEnableDisableMessage.products || [],
				selectionModel.products
			),
		});
	};

	const handleRemoveItem = (
		id: number,
		type: keyof ProductEnableDisableMessage
	) => {
		setProductEnableDisableMessage({
			...productEnableDisableMessage,
			[type]: Array.isArray(productEnableDisableMessage[type])
				? (productEnableDisableMessage[type] as EnableDisableModel[]).filter(
						(item) => item.id !== id
				  )
				: productEnableDisableMessage[type],
		});
	};

	const handleClearAll = () => {
		setProductEnableDisableMessage({
			requestId: null,
			filteredDeviceNames: [],
			categories: [],
			productTypes: [],
			products: [],
			tariffs: [],
		});
	};

	const handleEnableClick = () => {
		handleEnableDisable(true);
		setSelectionModel({
			categories: [],
			productTypes: [],
			products: [],
			tariffs: [],
		});
	};

	const handleDisableClick = () => {
		handleEnableDisable(false);
		setSelectionModel({
			categories: [],
			productTypes: [],
			products: [],
			tariffs: [],
		});
	};

	const handleEnableContractClick = () => {
		handleEnableDisableContractCreate(true);
		setSelectionModel({
			categories: [],
			productTypes: [],
			products: [],
			tariffs: [],
		});
	};

	const handleDisableContractClick = () => {
		handleEnableDisableContractCreate(false);
		setSelectionModel({
			categories: [],
			productTypes: [],
			products: [],
			tariffs: [],
		});
	};

	const handleInstitutionChange = (event: SelectChangeEvent<number>) => {
		const institutionId = event.target.value as number;

		// Reset relevant selections and table visibility when clearing institution selection
		if (institutionId) {
			setSelectedInstitutionID(institutionId);
			setIsProductTypeTableVisible(false);
			setIsProductTableVisible(true);
			setIsTariffTableVisible(false);
		} else {
			// Clear selection if institution is deselected
			setSelectedInstitutionID(null);
			setSelectedProductTypeID(null);
			setIsProductTypeTableVisible(false);
			setIsProductTableVisible(false);
			setIsTariffTableVisible(false);
		}

		setSelectedCategoryID(null);
		setSelectedProductTypeID(null);
		setSelectedProductID(null);

		setSelectionModel({
			categories: [],
			productTypes: [],
			products: [],
			tariffs: [],
		});
	};

	const getEnabledTariffsLabel = (productID: number) => {
		const tariffs = productDeviceModel?.tariffs.filter(
			(x) => x.tariff.productID === productID
		);
		return (
			<>
				{tariffs?.filter((x) => x.tariff.isActive).length} / {tariffs?.length}
			</>
		);
	};

	const getInstitutionLabel = (institutionID: number) => {
		const institution = productDeviceModel?.institutions.find(
			(x) => x.institution.institutionID === institutionID
		);
		if (!!institution) {
			return <>{institution.institution.displayName}</>;
		}

		return <>?</>;
	};

	const getProductInstitutionLabel = (productID: number) => {
		const product = productDeviceModel?.products.find(
			(x) => x.product.productID === productID
		);
		if (!!product) {
			const institution = productDeviceModel?.institutions.find(
				(x) => x.institution.institutionID === product?.product.institutionID
			);
			if (!!institution) {
				return <>{institution.institution.displayName}</>;
			}
		}

		return <>?</>;
	};

	interface StyledTableRowProps extends TableRowProps {
		isSelected: boolean;
	}
	const StyledTableRow = styled(TableRow)<StyledTableRowProps>`
		background-color: ${({ isSelected }) =>
			isSelected ? "#ebf1f7" : "transparent"};
		&:hover {
			background-color: ${({ isSelected, theme }) =>
				isSelected ? "#c6cacf" : theme.palette.action.selected};
		}
	`;

	const getMessageSummary = () => {
		const items: string[] = [];
		if (!!productEnableDisableMessage) {
			if ((productEnableDisableMessage.categories || []).length > 0) {
				items.push(`moduly: ${productEnableDisableMessage.categories.length}`);
			}
			if ((productEnableDisableMessage.productTypes || []).length > 0) {
				items.push(`typy: ${productEnableDisableMessage.productTypes.length}`);
			}
			if ((productEnableDisableMessage.products || []).length > 0) {
				items.push(`produkty: ${productEnableDisableMessage.products.length}`);
			}
			if ((productEnableDisableMessage.tariffs || []).length > 0) {
				items.push(`tarify: ${productEnableDisableMessage.tariffs.length}`);
			}
		}

		return items;
	};

	// Function to render category table
	const renderCategoryTable = (
		categories: EnableDisableModel[],
		categoryData: CategoryDeviceModel[]
	) => (
		<Stack>
			<TableContainer component={Paper}>
				<Typography fontSize="1rem" fontWeight="bold" m={1}>
					Moduly
				</Typography>
				<Table size="small" sx={{ my: 1 }}>
					<TableHead>
						<TableRow>
							<TableCell sx={{ flex: 1 }}>
								<strong>Název</strong>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{categories.length === 0 ? (
							<TableRow>
								<TableCell colSpan={1} align="center">
									<Typography variant="subtitle2">Žádný výběr</Typography>
								</TableCell>
							</TableRow>
						) : (
							categories.map((category) => {
								const fullCategory = categoryData.find(
									(dataItem) => dataItem.category.categoryID === category.id
								);

								return (
									<>
										<TableRow key={category.id}>
											<TableCell>
												{getCategoryDisplayName(
													fullCategory?.category.categoryID ?? -1
												)}
											</TableCell>
										</TableRow>
										{fullCategory?.devices.map(
											(e) =>
												deviceForm.devices.find((x) => x.name === e.deviceName)
													?.isChecked && (
													<TableRow>
														<TableCell colSpan={1}>
															<Stack
																direction="row"
																alignItems="center"
																justifyContent="space-between"
																spacing={1}
																ml={5}
															>
																<Typography>{e.deviceName}</Typography>
																<Stack
																	direction="row"
																	alignItems="center"
																	spacing={1}
																>
																	{e.isEnabled ? (
																		<CheckCircleIcon
																			fontSize="small"
																			color="success"
																		/>
																	) : (
																		<CancelIcon
																			fontSize="small"
																			color="error"
																		/>
																	)}
																	<span>→</span>
																	{category.doEnable ? (
																		<CheckCircleIcon
																			fontSize="small"
																			color="success"
																		/>
																	) : (
																		<CancelIcon
																			fontSize="small"
																			color="error"
																		/>
																	)}
																</Stack>
															</Stack>
														</TableCell>
													</TableRow>
												)
										)}
									</>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Stack>
	);

	// Function to render Product Type table
	const renderProductTypeTable = (
		productTypes: EnableDisableModel[],
		productTypeData: ProductTypeDeviceModel[]
	) => (
		<Stack>
			<TableContainer component={Paper}>
				<Typography fontSize="1rem" fontWeight="bold" m={1}>
					Typy
				</Typography>
				<Table size="small" sx={{ my: 1 }}>
					<TableHead>
						<TableRow>
							<TableCell sx={{ flex: 1 }}>
								<strong>Název</strong>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{productTypes.length === 0 ? (
							<TableRow>
								<TableCell colSpan={1} align="center">
									<Typography variant="subtitle2">Žádný výběr</Typography>
								</TableCell>
							</TableRow>
						) : (
							productTypes.map((productType) => {
								const fullProductType = productTypeData.find(
									(dataItem) =>
										dataItem.productType.productTypeID === productType.id
								);

								return (
									<>
										<TableRow key={productType.id}>
											<TableCell>
												{getProductTypeDisplayName(
													fullProductType?.productType.productTypeID ?? -1
												) ?? fullProductType?.productType.name}
											</TableCell>
										</TableRow>
										{fullProductType?.devices.map(
											(e) =>
												deviceForm.devices.find((x) => x.name === e.deviceName)
													?.isChecked && (
													<TableRow>
														<TableCell colSpan={1}>
															<Stack
																direction="row"
																alignItems="center"
																justifyContent="space-between"
																spacing={1}
																ml={5}
															>
																<Typography>{e.deviceName}</Typography>
																<Stack
																	direction="row"
																	alignItems="center"
																	spacing={1}
																>
																	{e.isEnabled ? (
																		<CheckCircleIcon
																			fontSize="small"
																			color="success"
																		/>
																	) : (
																		<CancelIcon
																			fontSize="small"
																			color="error"
																		/>
																	)}
																	<span>→</span>
																	{productType.doEnable ? (
																		<CheckCircleIcon
																			fontSize="small"
																			color="success"
																		/>
																	) : (
																		<CancelIcon
																			fontSize="small"
																			color="error"
																		/>
																	)}
																</Stack>
															</Stack>
														</TableCell>
													</TableRow>
												)
										)}
									</>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Stack>
	);

	// Function to render Product table
	const renderProductTable = (
		products: ProductEnableDisableModel[],
		productData: ProductDeviceModel[]
	) => (
		<Stack>
			<TableContainer component={Paper}>
				<Typography fontSize="1rem" fontWeight="bold" m={1}>
					Produkty
				</Typography>
				<Table size="small" sx={{ my: 1 }}>
					<TableHead>
						<TableRow>
							<TableCell>
								<strong>Název</strong>
							</TableCell>
							<TableCell sx={{ flex: 1 }}>
								<strong>Typ</strong>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{products.length === 0 ? (
							<TableRow>
								<TableCell colSpan={2} align="center">
									<Typography variant="subtitle2">Žádný výběr</Typography>
								</TableCell>
							</TableRow>
						) : (
							products.map((product) => {
								const fullProduct = productData.find(
									(dataItem) => dataItem.product.productID === product.id
								);

								return (
									<>
										<TableRow key={product.id}>
											<TableCell>{fullProduct?.product.name}</TableCell>
											<TableCell>
												{getProductTypeDisplayName(
													productDeviceModel?.productTypes.find(
														(x) =>
															x.productType.productTypeID ===
															fullProduct?.product.productTypeID
													)?.productType?.productTypeID ?? -1
												)}
											</TableCell>
										</TableRow>
										{fullProduct?.devices.map(
											(e) =>
												deviceForm.devices.find((x) => x.name === e.deviceName)
													?.isChecked && (
													<TableRow>
														<TableCell colSpan={2}>
															<Stack
																direction="row"
																alignItems="center"
																justifyContent="space-between"
																spacing={1}
																ml={5}
															>
																<Typography>{e.deviceName}</Typography>
																<Stack
																	direction="row"
																	alignItems="center"
																	spacing={5}
																>
																	{product.doEnableContractCreate !==
																		undefined &&
																		product.doEnableContractCreate !== null && (
																			<Stack
																				direction="row"
																				alignItems="center"
																				spacing={1}
																			>
																				<HistoryEduIcon
																					fontSize="small"
																					color={
																						e.isContractCreateEnabled
																							? "success"
																							: "error"
																					}
																				/>
																				<span>→</span>
																				<HistoryEduIcon
																					fontSize="small"
																					color={
																						product.doEnableContractCreate
																							? "success"
																							: "error"
																					}
																				/>
																			</Stack>
																		)}
																	{product.doEnable !== undefined &&
																		product.doEnable !== null && (
																			<Stack
																				direction="row"
																				alignItems="center"
																				spacing={1}
																			>
																				{e.isEnabled ? (
																					<CheckCircleIcon
																						fontSize="small"
																						color="success"
																					/>
																				) : (
																					<CancelIcon
																						fontSize="small"
																						color="error"
																					/>
																				)}
																				<span>→</span>
																				{product.doEnable ? (
																					<CheckCircleIcon
																						fontSize="small"
																						color="success"
																					/>
																				) : (
																					<CancelIcon
																						fontSize="small"
																						color="error"
																					/>
																				)}
																			</Stack>
																		)}
																</Stack>
															</Stack>
														</TableCell>
													</TableRow>
												)
										)}
									</>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Stack>
	);

	// Function to render Tariff table
	const renderTariffTable = (
		tariffs: EnableDisableModel[],
		tariffData: TariffDeviceModel[]
	) => (
		<Stack>
			<TableContainer component={Paper}>
				<Typography fontSize="1rem" fontWeight="bold" m={1}>
					Tarify
				</Typography>
				<Table size="small" sx={{ my: 1 }}>
					<TableHead>
						<TableRow>
							<TableCell>
								<strong>Název</strong>
							</TableCell>
							<TableCell sx={{ flex: 1 }}>
								<strong>Produkt</strong>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{tariffs.length === 0 ? (
							<TableRow>
								<TableCell colSpan={2} align="center">
									<Typography variant="subtitle2">Žádný výběr</Typography>
								</TableCell>
							</TableRow>
						) : (
							tariffs.map((tariff) => {
								const fullTariff = tariffData.find(
									(dataItem) => dataItem.tariff.tariffID === tariff.id
								);

								return (
									<>
										<TableRow key={tariff.id}>
											<TableCell>{fullTariff?.tariff.name}</TableCell>
											<TableCell>
												{
													productDeviceModel?.products.find(
														(x) =>
															x.product.productID ===
															fullTariff?.tariff.productID
													)?.product?.name
												}
											</TableCell>
										</TableRow>
										{fullTariff?.devices.map(
											(e) =>
												deviceForm.devices.find((x) => x.name === e.deviceName)
													?.isChecked && (
													<TableRow>
														<TableCell colSpan={2}>
															<Stack
																direction="row"
																alignItems="center"
																justifyContent="space-between"
																spacing={1}
																ml={5}
															>
																<Typography>{e.deviceName}</Typography>
																<Stack
																	direction="row"
																	alignItems="center"
																	spacing={1}
																>
																	{e.isEnabled ? (
																		<CheckCircleIcon
																			fontSize="small"
																			color="success"
																		/>
																	) : (
																		<CancelIcon
																			fontSize="small"
																			color="error"
																		/>
																	)}
																	<span>→</span>
																	{tariff.doEnable ? (
																		<CheckCircleIcon
																			fontSize="small"
																			color="success"
																		/>
																	) : (
																		<CancelIcon
																			fontSize="small"
																			color="error"
																		/>
																	)}
																</Stack>
															</Stack>
														</TableCell>
													</TableRow>
												)
										)}
									</>
								);
							})
						)}
					</TableBody>
				</Table>
			</TableContainer>
		</Stack>
	);

	const renderDeviceFirstView = () => (
		<Stack>
			{deviceForm.devices.map((device) => {
				if (!device.isChecked) return null;

				const associatedCategories = (
					productEnableDisableMessage.categories || []
				).filter((category) =>
					productDeviceModel?.categories.some(
						(cat) =>
							cat.category.categoryID === category.id &&
							cat.devices.some((d) => d.deviceName === device.name)
					)
				);

				const associatedProductTypes = (
					productEnableDisableMessage.productTypes || []
				).filter((productType) =>
					productDeviceModel?.productTypes.some(
						(pt) =>
							pt.productType.productTypeID === productType.id &&
							pt.devices.some((d) => d.deviceName === device.name)
					)
				);

				const associatedProducts = (
					productEnableDisableMessage.products || []
				).filter((product) =>
					productDeviceModel?.products.some(
						(prod) =>
							prod.product.productID === product.id &&
							prod.devices.some((d) => d.deviceName === device.name)
					)
				);

				const associatedTariffs = (
					productEnableDisableMessage.tariffs || []
				).filter((tariff) =>
					productDeviceModel?.tariffs.some(
						(tar) =>
							tar.tariff.tariffID === tariff.id &&
							tar.devices.some((d) => d.deviceName === device.name)
					)
				);

				return (
					<Box key={device.name} sx={{ mb: 3 }}>
						<TableContainer component={Paper}>
							<Typography
								fontSize="1.2rem"
								fontWeight="bold"
								sx={{ m: 0.5, ml: 1 }}
							>
								{device.name}
							</Typography>
							{/* Categories */}
							{associatedCategories.length > 0 && (
								<Box sx={{ mb: 2 }}>
									<Typography fontSize="1rem" sx={{ m: 1 }}>
										<strong>Moduly</strong>
									</Typography>

									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell sx={{ flex: 1 }}>
													<strong>Název</strong>
												</TableCell>
												<TableCell />
											</TableRow>
										</TableHead>
										<TableBody>
											{associatedCategories.map((category) => {
												const fullCategory =
													productDeviceModel?.categories.find(
														(cat) => cat.category.categoryID === category.id
													);
												const isEnabled = fullCategory?.devices.some(
													(d) => d.deviceName === device.name && d.isEnabled
												);

												return (
													<TableRow key={category.id}>
														<TableCell>
															{getCategoryDisplayName(category.id)}
														</TableCell>
														<TableCell>
															<Stack
																direction="row"
																alignItems="center"
																justifyContent="end"
															>
																<Stack
																	direction="row"
																	alignItems="center"
																	spacing={1}
																>
																	{isEnabled ? (
																		<CheckCircleIcon
																			fontSize="small"
																			color="success"
																		/>
																	) : (
																		<CancelIcon
																			fontSize="small"
																			color="error"
																		/>
																	)}
																	<span>→</span>
																	{category.doEnable ? (
																		<CheckCircleIcon
																			fontSize="small"
																			color="success"
																		/>
																	) : (
																		<CancelIcon
																			fontSize="small"
																			color="error"
																		/>
																	)}
																</Stack>
															</Stack>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</Box>
							)}
							{/* Product Types */}
							{associatedProductTypes.length > 0 && (
								<Box sx={{ mb: 2 }}>
									<Typography fontSize="1rem" sx={{ m: 1 }}>
										<strong>Typy</strong>
									</Typography>
									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell>
													<strong>Název</strong>
												</TableCell>
												<TableCell sx={{ flex: 1 }} />
											</TableRow>
										</TableHead>
										<TableBody>
											{associatedProductTypes.map((productType) => {
												const fullProductType =
													productDeviceModel?.productTypes.find(
														(pt) =>
															pt.productType.productTypeID === productType.id
													);
												const isEnabled = fullProductType?.devices.some(
													(d) => d.deviceName === device.name && d.isEnabled
												);

												return (
													<TableRow key={productType.id}>
														<TableCell>
															{getProductTypeDisplayName(productType.id)}
														</TableCell>
														<TableCell>
															<Stack
																direction="row"
																alignItems="center"
																justifyContent="end"
															>
																<Stack
																	direction="row"
																	alignItems="center"
																	spacing={1}
																>
																	{isEnabled ? (
																		<CheckCircleIcon
																			fontSize="small"
																			color="success"
																		/>
																	) : (
																		<CancelIcon
																			fontSize="small"
																			color="error"
																		/>
																	)}
																	<span>→</span>
																	{productType.doEnable ? (
																		<CheckCircleIcon
																			fontSize="small"
																			color="success"
																		/>
																	) : (
																		<CancelIcon
																			fontSize="small"
																			color="error"
																		/>
																	)}
																</Stack>
															</Stack>
														</TableCell>
													</TableRow>
												);
											})}
										</TableBody>
									</Table>
								</Box>
							)}
							{/* Products */}
							{associatedProducts.length > 0 && (
								<Box sx={{ mb: 2 }}>
									<Typography fontSize="1rem" sx={{ m: 1 }}>
										<strong>Produkty</strong>
									</Typography>

									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell width={500}>
													<strong>Název</strong>
												</TableCell>
												<TableCell>
													<strong>Typ</strong>
												</TableCell>
												<TableCell sx={{ flex: 1 }} />
											</TableRow>
										</TableHead>
										<TableBody>
											{associatedProducts.map((product) => {
												const fullProduct = productDeviceModel?.products.find(
													(prod) => prod.product.productID === product.id
												);
												const isEnabled = fullProduct?.devices.some(
													(d) => d.deviceName === device.name && d.isEnabled
												);
												const isContractCreateEnabled =
													fullProduct?.devices.some(
														(d) =>
															d.deviceName === device.name &&
															d.isContractCreateEnabled
													);

												return (
													<>
														<TableRow key={product.id}>
															<TableCell>{fullProduct?.product.name}</TableCell>
															<TableCell>
																{getProductTypeDisplayName(
																	productDeviceModel?.productTypes.find(
																		(x) =>
																			x.productType.productTypeID ===
																			fullProduct?.product.productTypeID
																	)?.productType?.productTypeID ?? -1
																)}
															</TableCell>
															<TableCell>
																<Stack
																	direction="row"
																	alignItems="center"
																	justifyContent="end"
																>
																	<Stack
																		direction="row"
																		alignItems="center"
																		spacing={5}
																	>
																		{product.doEnableContractCreate !==
																			undefined &&
																			product.doEnableContractCreate !==
																				null && (
																				<Stack
																					direction="row"
																					alignItems="center"
																					spacing={1}
																				>
																					<HistoryEduIcon
																						fontSize="small"
																						color={
																							isContractCreateEnabled
																								? "success"
																								: "error"
																						}
																					/>
																					<span>→</span>
																					<HistoryEduIcon
																						fontSize="small"
																						color={
																							product.doEnableContractCreate
																								? "success"
																								: "error"
																						}
																					/>
																				</Stack>
																			)}
																		{product.doEnable !== undefined &&
																			product.doEnable !== null && (
																				<Stack
																					direction="row"
																					alignItems="center"
																					spacing={1}
																				>
																					{isEnabled ? (
																						<CheckCircleIcon
																							fontSize="small"
																							color="success"
																						/>
																					) : (
																						<CancelIcon
																							fontSize="small"
																							color="error"
																						/>
																					)}
																					<span>→</span>
																					{product.doEnable ? (
																						<CheckCircleIcon
																							fontSize="small"
																							color="success"
																						/>
																					) : (
																						<CancelIcon
																							fontSize="small"
																							color="error"
																						/>
																					)}
																				</Stack>
																			)}
																	</Stack>
																</Stack>
															</TableCell>
														</TableRow>
													</>
												);
											})}
										</TableBody>
									</Table>
								</Box>
							)}
							{/* Tariffs */}
							{associatedTariffs.length > 0 && (
								<Box sx={{ mb: 2 }}>
									<Typography fontSize="1rem" sx={{ m: 1 }}>
										<strong>Tarify</strong>
									</Typography>

									<Table size="small">
										<TableHead>
											<TableRow>
												<TableCell width={500}>
													<strong>Název</strong>
												</TableCell>
												<TableCell>
													<strong>Produkt</strong>
												</TableCell>
												<TableCell sx={{ flex: 1 }} />
											</TableRow>
										</TableHead>
										<TableBody>
											{associatedTariffs.map((tariff) => {
												const fullTariff = productDeviceModel?.tariffs.find(
													(tar) => tar.tariff.tariffID === tariff.id
												);
												const isEnabled = fullTariff?.devices.some(
													(d) => d.deviceName === device.name && d.isEnabled
												);

												return (
													<>
														<TableRow key={tariff.id}>
															<TableCell>{fullTariff?.tariff.name}</TableCell>
															<TableCell>
																{
																	productDeviceModel?.products.find(
																		(x) =>
																			x.product.productID ===
																			fullTariff?.tariff.productID
																	)?.product?.name
																}
															</TableCell>
															<TableCell>
																<Stack
																	direction="row"
																	alignItems="center"
																	justifyContent="end"
																>
																	<Stack
																		direction="row"
																		alignItems="center"
																		spacing={1}
																	>
																		{isEnabled ? (
																			<CheckCircleIcon
																				fontSize="small"
																				color="success"
																			/>
																		) : (
																			<CancelIcon
																				fontSize="small"
																				color="error"
																			/>
																		)}
																		<span>→</span>
																		{tariff.doEnable ? (
																			<CheckCircleIcon
																				fontSize="small"
																				color="success"
																			/>
																		) : (
																			<CancelIcon
																				fontSize="small"
																				color="error"
																			/>
																		)}
																	</Stack>
																</Stack>
															</TableCell>
														</TableRow>
													</>
												);
											})}
										</TableBody>
									</Table>
								</Box>
							)}
						</TableContainer>
					</Box>
				);
			})}
		</Stack>
	);

	// Filtered product types based on selected category
	const filteredProductTypes =
		productDeviceModel?.productTypes.filter(
			(pt) => pt.productType.categoryID === selectedCategoryID
		) || [];

	// Filtered products based on selected product type or selected institution
	const filteredProducts =
		productDeviceModel?.products.filter((prod) => {
			if (selectedInstitutionID !== null) {
				return prod.product.institutionID === selectedInstitutionID;
			}
			return prod.product.productTypeID === selectedProductTypeID;
		}) || [];

	// Filtered tariffs based on selected product
	const filteredTariffs =
		productDeviceModel?.tariffs.filter(
			(tariff) => tariff.tariff.productID === selectedProductID
		) || [];

	const handleRowClick = (
		id: number,
		level: "category" | "productType" | "product"
	) => {
		const rowKey = `${level}-${id}`; // Generate unique key based on level and ID
		setExpandedRows((prev) => {
			const newExpandedRows = new Set(prev);
			if (newExpandedRows.has(rowKey)) {
				newExpandedRows.delete(rowKey);
			} else {
				newExpandedRows.add(rowKey);
			}
			return newExpandedRows;
		});

		if (level === "category") {
			setSelectedCategoryID(id);
			setSelectedProductTypeID(null);
			setSelectedProductID(null);
		} else if (level === "productType") {
			setSelectedProductTypeID(id);
			setSelectedProductID(null);
		} else if (level === "product") {
			setSelectedProductID(id);
		}
	};

	const confirmCanBeOpened = () => {
		if (deviceForm.devices.filter((x) => x.isChecked).length === 0) {
			showSnackbar(
				t(Resources.Errors.General.Instance.NoInstanceSelected),
				"warning"
			);
			return false;
		}
		return true;
	};

	const handleReasonTextFieldChange = (
		event: ChangeEvent<HTMLInputElement>
	) => {
		const newValue = event.target.value;
		setReasonText(newValue);
		setProductEnableDisableMessage({
			...productEnableDisableMessage,
			reason: newValue,
		});
	};

	const [reasonText, setReasonText] = useState("");
	const debouncedReasonText = useDebounce(reasonText, 300);

	useEffect(() => {
		setReasonText(debouncedReasonText);
	}, [debouncedReasonText]);

	const [isDeviceFirstView, setIsDeviceFirstView] = useState(true);

	// biome-ignore lint/correctness/useExhaustiveDependencies: callback factory pattern intentionally captures outer scope values
	const getConfirmDialogContent = useMemo(
		() => () =>
			(
				<Stack mt={1} spacing={1}>
					<TextField
						name="reason"
						label="Důvod změny"
						variant="outlined"
						size="small"
						value={reasonText}
						inputProps={{ maxLength: 200 }}
						onChange={handleReasonTextFieldChange}
						sx={{ flex: 1 }}
					/>
					<Stack direction="row" justifyContent="end">
						<Button
							endIcon={<AccountTreeIcon />}
							variant="outlined"
							onClick={() => setIsDeviceFirstView(!isDeviceFirstView)}
							size="small"
						>
							{isDeviceFirstView ? "Dle instance" : "Dle produktů"}
						</Button>
					</Stack>
					{isDeviceFirstView ? (
						renderDeviceFirstView()
					) : (
						<>
							{renderCategoryTable(
								productEnableDisableMessage.categories || [],
								productDeviceModel?.categories || []
							)}
							{renderProductTypeTable(
								productEnableDisableMessage.productTypes || [],
								productDeviceModel?.productTypes || []
							)}
							{renderProductTable(
								productEnableDisableMessage.products || [],
								productDeviceModel?.products || []
							)}
							{renderTariffTable(
								productEnableDisableMessage.tariffs || [],
								productDeviceModel?.tariffs || []
							)}
						</>
					)}
				</Stack>
			),
		[
			isDeviceFirstView,
			selectionModel,
			reasonText,
			productEnableDisableMessage,
			deviceForm,
		]
	);

	const handleInvertAll = () => {
		setProductEnableDisableMessage({
			...productEnableDisableMessage,
			categories: (productEnableDisableMessage.categories || []).map(
				(c: EnableDisableModel) => ({
					...c,
					doEnable: typeof c.doEnable === "boolean" ? !c.doEnable : true,
				})
			),
			productTypes: (productEnableDisableMessage.productTypes || []).map(
				(pt: EnableDisableModel) => ({
					...pt,
					doEnable: typeof pt.doEnable === "boolean" ? !pt.doEnable : true,
				})
			),
			products: (productEnableDisableMessage.products || []).map(
				(p: ProductEnableDisableModel) => ({
					...p,
					doEnable: typeof p.doEnable === "boolean" ? !p.doEnable : true,
					doEnableContractCreate:
						typeof p.doEnableContractCreate === "boolean"
							? !p.doEnableContractCreate
							: p.doEnableContractCreate,
				})
			),
			tariffs: (productEnableDisableMessage.tariffs || []).map(
				(t: EnableDisableModel) => ({
					...t,
					doEnable: typeof t.doEnable === "boolean" ? !t.doEnable : true,
				})
			),
		});
	};

	return (
		<Stack>
			{/* // Main Table */}
			<Box mb={1}>
				<Typography variant="h6" component="div">
					Data
				</Typography>
				<Divider />
			</Box>
			<Stack
				direction="row"
				justifyContent="space-between"
				alignItems="center"
				mb={1}
			>
				<Stack direction="row" columnGap={1} alignItems="center">
					<Button
						variant="text"
						color="primary"
						onClick={handleEnableClick}
						disabled={
							selectionModel.categories.length === 0 &&
							selectionModel.productTypes.length === 0 &&
							selectionModel.products.length === 0 &&
							selectionModel.tariffs.length === 0
						}
					>
						Zapnout
					</Button>
					<Button
						variant="text"
						color="secondary"
						onClick={handleDisableClick}
						disabled={
							selectionModel.categories.length === 0 &&
							selectionModel.productTypes.length === 0 &&
							selectionModel.products.length === 0 &&
							selectionModel.tariffs.length === 0
						}
					>
						Vypnout
					</Button>
					<Box borderLeft="1px solid gray" sx={{ height: 20 }} />
					<Button
						variant="text"
						color="primary"
						onClick={handleEnableContractClick}
						disabled={selectionModel.products.length === 0}
					>
						Zapnout sjednání
					</Button>
					<Button
						variant="text"
						color="secondary"
						onClick={handleDisableContractClick}
						disabled={selectionModel.products.length === 0}
					>
						Vypnout sjednání
					</Button>
					<Box borderLeft="1px solid gray" sx={{ height: 20 }} />
					<Stack direction="row" spacing={0.1} alignItems="center">
						<BlConfirmButton
							iconButton
							icon={<SaveIcon />}
							modalTitle={"Zpracování"}
							modalContent={getConfirmDialogContent()}
							modalMaxWidth="1024px"
							onResult={(result: any) => {
								if (!!result) {
									onEnableDisable(productEnableDisableMessage);
								}
							}}
							disabled={getMessageSummary().length === 0}
							onClick={confirmCanBeOpened}
							variant="primary"
							tooltip={"Zpracovat vybrané změny"}
							tooltipPlacement="top"
						>
							Potvrdit
						</BlConfirmButton>
						<BlConfirmButton
							iconButton
							icon={<SwapHorizIcon />}
							modalTitle={"Invertovat vybrané změny?"}
							modalContent={""}
							onResult={(result: any) => {
								if (!!result) {
									handleInvertAll();
								}
							}}
							disabled={getMessageSummary().length === 0 || !isHistoryLoaded}
							variant="primary"
							tooltip={"Invertovat vybrané změny u historické akce"}
							tooltipPlacement="top"
						>
							Invertovat
						</BlConfirmButton>

						<BlTooltip title="Zrušit vybrané změny" placement="top">
							<IconButton
								onClick={(e) => {
									e.stopPropagation();
									handleClearAll();
								}}
								disabled={getMessageSummary().length === 0}
								color="primary"
							>
								<ClearAllIcon />
							</IconButton>
						</BlTooltip>
					</Stack>
				</Stack>

				<Box>
					<Typography sx={{ color: "text.secondary", flex: 1 }}>
						{getMessageSummary().length > 0
							? <><strong>Vybrané změny</strong> – </>
							: "Žádný výběr"}
						{getMessageSummary().join(", ")}
					</Typography>
				</Box>

				<FormControl
					variant="outlined"
					style={{ minWidth: 200, marginLeft: 16 }}
				>
					<InputLabel size="small">Instituce</InputLabel>
					<Select
						value={selectedInstitutionID ?? ""}
						onChange={handleInstitutionChange}
						label="Instituce"
						size="small"
						MenuProps={{
							PaperProps: {
								style: {
									maxHeight: "26.5rem",
								},
							},
						}}
						sx={{ width: 400 }}
					>
						<MenuItem
							value=""
							sx={{
								paddingY: 0.1,
							}}
						>
							<em>Nevybráno</em>
						</MenuItem>
						{productDeviceModel?.institutions
							.slice()
							.sort((a, b) => {
								return (a.institution.displayName || "").localeCompare(
									b.institution.displayName || ""
								);
							})
							.map((inst) => (
								<MenuItem
									key={inst.institution.institutionID}
									value={inst.institution.institutionID}
									sx={{
										paddingY: 0.1,
									}}
								>
									{inst.institution.displayName} ({inst.institution.name})
								</MenuItem>
							))}
					</Select>
				</FormControl>
			</Stack>
			{/* Main Dynamic Tree Table */}
			<TableContainer component={Paper} style={{ marginTop: 1 }}>
				<Table size="small">
					{/* <TableHead>
						<TableRow>
							<TableCell width={50}>
								<strong>Modul</strong>
							</TableCell>
							<TableCell width={100}></TableCell>
							<TableCell>
								<strong>Name</strong>
							</TableCell>
							<TableCell>
								<strong>Description</strong>
							</TableCell>
						</TableRow>
					</TableHead> */}
					<TableBody>
						{/* Render categories */}
						{productDeviceModel?.categories.map((category, _i) => {
							// Filter product types under this category
							const categoryProductTypes =
								productDeviceModel.productTypes.filter(
									(pt) =>
										pt.productType.categoryID === category.category.categoryID
								);

							// Filter products for the selected institution (if any)
							const filteredProductTypes =
								selectedInstitutionID === null
									? categoryProductTypes // Show all product types when no institution is selected
									: categoryProductTypes.filter((pt) =>
											productDeviceModel.products.some(
												(prod) =>
													prod.product.productTypeID ===
														pt.productType.productTypeID &&
													prod.product.institutionID === selectedInstitutionID
											)
									  );

							// Only show the category if it has relevant product types
							if (filteredProductTypes.length === 0) {
								return null; // Hide category if no relevant product types
							}

							const categoryKey = `category-${category.category.categoryID}`;
							const isCategoryExpanded = expandedRows.has(categoryKey);

							const isCategorySelected = selectionModel.categories.includes(
								category.category.categoryID
							);
							const areAllCategoryChildrenSelected =
								filteredProductTypes.length > 0 &&
								filteredProductTypes.every((pt) =>
									selectionModel.productTypes.includes(
										pt.productType.productTypeID
									)
								);
							const isCategoryIndeterminate =
								!areAllCategoryChildrenSelected &&
								filteredProductTypes.some((pt) =>
									selectionModel.productTypes.includes(
										pt.productType.productTypeID
									)
								);

							const categoryMessage = (
								productEnableDisableMessage.categories || []
							).find((cat) => cat.id === category.category.categoryID);

							const CategoryTooltipContent = ({
								category,
							}: {
								category: Category;
							}) => {
								return (
									<Box sx={{ maxWidth: 600, p: 1 }}>
										<Typography
											variant="subtitle1"
											sx={{ fontWeight: "bold", mb: 1 }}
										>
											{getCategoryDisplayName(category.categoryID)}
										</Typography>
										<Typography
											variant="body2"
											sx={{ fontWeight: "bold", mb: 1 }}
										>
											Databázová hodnota (Insurance.Categories)
										</Typography>
										<Typography sx={{ fontSize: "0.7rem" }} mb={2} ml={1}>
											IsActive:{" "}
											{category.isActive ? (
												<CheckCircleIcon
													sx={{ fontSize: "0.7rem", color: "success.main" }}
												/>
											) : (
												<CancelIcon
													sx={{ fontSize: "0.7rem", color: "error.main" }}
												/>
											)}
										</Typography>
										<Typography
											variant="body2"
											sx={{ fontWeight: "bold", mb: 1 }}
										>
											Závislé features (všechny)
										</Typography>
										<CategoryFeatureTable
											features={category.requiredFeatures}
										/>

										<Typography
											variant="body2"
											sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
										>
											Závislé features (alespoň jedna)
										</Typography>
										<CategoryFeatureTable
											features={category.optionalFeatures}
										/>

										<Typography
											variant="body2"
											sx={{ fontWeight: "bold", mt: 2, mb: 1 }}
										>
											Další features
										</Typography>
										<CategoryFeatureTable features={category.otherFeatures} />
									</Box>
								);
							};

							return (
								<>
									{/* Category Row */}
									<TableRow
										key={categoryKey}
										sx={{
											borderTop: _i !== 0 ? "2px solid #d3d3d3" : "",
											backgroundColor: !!categoryMessage
												? categoryMessage.doEnable
													? "rgb(250, 255, 248)"
													: "rgb(255, 248, 248)"
												: "",
										}}
									>
										<TableCell width={150}>
											<Stack direction="row" spacing={1} alignItems="center">
												<Checkbox
													checked={areAllCategoryChildrenSelected}
													indeterminate={isCategoryIndeterminate}
													onChange={() =>
														handleSelectAll(
															"productTypes",
															filteredProductTypes.map(
																(pt) => pt.productType.productTypeID
															)
														)
													}
													title="Vybrat všechny potomky"
													sx={{ p: 0.1 }}
													icon={<FactCheckOutlinedIcon />}
													indeterminateIcon={<FactCheckOutlinedIcon />}
													checkedIcon={<FactCheckCircleIcon />}
												/>
												<Checkbox
													checked={isCategorySelected}
													onChange={() =>
														handleSelectionChange(
															category.category.categoryID,
															"categories"
														)
													}
													title="Vybrat"
													sx={{ p: 0.1 }}
												/>
												{deviceForm.devices.length === 1 ? (
													category.category.isActive ? (
														<CheckCircleIcon color="success" fontSize="small" />
													) : (
														<CancelIcon color="error" fontSize="small" />
													)
												) : (
													<QuestionMarkIcon color="disabled" fontSize="small" />
												)}
												{!!categoryMessage ? (
													categoryMessage.doEnable ? (
														<>
															<span>→</span>
															<CheckCircleIcon
																color="success"
																fontSize="small"
															/>
														</>
													) : (
														<>
															<span>→</span>
															<CancelIcon color="error" fontSize="small" />
														</>
													)
												) : (
													<></>
												)}
											</Stack>
										</TableCell>
										<TableCell width={50}>
											<Button
												size="small"
												onClick={() =>
													handleRowClick(
														category.category.categoryID,
														"category"
													)
												}
											>
												{isCategoryExpanded ? (
													<KeyboardArrowDownIcon />
												) : (
													<KeyboardArrowRightIcon />
												)}
											</Button>
										</TableCell>
										<TableCell width={500}>
											<Stack direction="row" spacing={0.5} alignItems="center">
												<Tooltip
													title={
														<CategoryTooltipContent
															category={category.category}
														/>
													}
													placement="right"
													enterDelay={500}
													componentsProps={{
														tooltip: {
															sx: {
																bgcolor: "background.paper",
																color: "text.primary",
																border: "1px solid",
																borderColor: "divider",
																boxShadow: 3,
																minWidth: "900px",
																maxWidth: "none",
															},
														},
													}}
												>
													<Stack sx={{ cursor: "help" }}>
														{deviceForm.devices.length === 1 &&
															(category.category.isEnabled ? (
																<CheckBoxSharpIcon
																	color="success"
																	fontSize="small"
																/>
															) : (
																<DisabledByDefaultIcon
																	color="error"
																	fontSize="small"
																/>
															))}
													</Stack>
												</Tooltip>
												<strong>
													{getCategoryDisplayName(category.category.categoryID)}
												</strong>
											</Stack>
										</TableCell>
										<TableCell>{category.category.description}</TableCell>
										<TableCell width={40}>
											{!!categoryMessage && (
												<Button
													color="error"
													onClick={() =>
														handleRemoveItem(
															category.category.categoryID,
															"categories"
														)
													}
													size="small"
													sx={{
														height: 24,
													}}
												>
													Zrušit
												</Button>
											)}
										</TableCell>
									</TableRow>
									{/* Render product types in a separate table */}
									{isCategoryExpanded && (
										<TableRow>
											<TableCell colSpan={5} style={{ padding: 0 }}>
												<Table size="small">
													{/* <TableHead>
														<TableRow>
															<TableCell width={100}>
																<strong>Typ</strong>
															</TableCell>
															<TableCell width={50}></TableCell>
															<TableCell>
																<strong>Product Type Name</strong>
															</TableCell>
															<TableCell>
																<strong>Description</strong>
															</TableCell>
														</TableRow>
													</TableHead> */}
													<TableBody>
														{filteredProductTypes.map((productType, _j) => {
															const productTypeKey = `productType-${productType.productType.productTypeID}`;
															const isProductTypeExpanded =
																expandedRows.has(productTypeKey);

															// Filter products under this product type
															const filteredProducts =
																productDeviceModel.products.filter(
																	(prod) =>
																		prod.product.productTypeID ===
																			productType.productType.productTypeID &&
																		(selectedInstitutionID === null ||
																			prod.product.institutionID ===
																				selectedInstitutionID)
																);

															const isProductTypeSelected =
																selectionModel.productTypes.includes(
																	productType.productType.productTypeID
																);
															const areAllProductTypeChildrenSelected =
																filteredProducts.length > 0 &&
																filteredProducts.every((prod) =>
																	selectionModel.products.includes(
																		prod.product.productID
																	)
																);
															const isProductTypeIndeterminate =
																!areAllProductTypeChildrenSelected &&
																filteredProducts.some((prod) =>
																	selectionModel.products.includes(
																		prod.product.productID
																	)
																);

															const productTypeMessage = (
																productEnableDisableMessage.productTypes || []
															).find(
																(pt) =>
																	pt.id ===
																	productType.productType.productTypeID
															);

															return (
																<>
																	{/* Product Type Row */}
																	<TableRow
																		key={productTypeKey}
																		sx={{
																			borderTop: "2px solid #eeeeee",
																			backgroundColor: !!productTypeMessage
																				? productTypeMessage.doEnable
																					? "rgb(250, 255, 248)"
																					: "rgb(255, 248, 248)"
																				: "",
																		}}
																	>
																		<TableCell width={200}>
																			<Stack
																				direction="row"
																				spacing={1}
																				alignItems="center"
																			>
																				<Checkbox
																					checked={
																						areAllProductTypeChildrenSelected
																					}
																					indeterminate={
																						isProductTypeIndeterminate
																					}
																					onChange={() =>
																						handleSelectAll(
																							"products",
																							filteredProducts.map(
																								(prod) => prod.product.productID
																							)
																						)
																					}
																					title="Vybrat všechny potomky"
																					sx={{ p: 0.1 }}
																					icon={<FactCheckOutlinedIcon />}
																					indeterminateIcon={
																						<FactCheckOutlinedIcon />
																					}
																					checkedIcon={<FactCheckCircleIcon />}
																				/>
																				<Checkbox
																					checked={isProductTypeSelected}
																					onChange={() =>
																						handleSelectionChange(
																							productType.productType
																								.productTypeID,
																							"productTypes"
																						)
																					}
																					title="Vybrat"
																					sx={{ p: 0.1 }}
																				/>
																				{deviceForm.devices.length === 1 ? (
																					productType.productType.isActive ? (
																						<CheckCircleIcon
																							color="success"
																							fontSize="small"
																						/>
																					) : (
																						<CancelIcon
																							color="error"
																							fontSize="small"
																						/>
																					)
																				) : (
																					<QuestionMarkIcon
																						color="disabled"
																						fontSize="small"
																					/>
																				)}
																				{!!productTypeMessage ? (
																					productTypeMessage.doEnable ? (
																						<>
																							<span>→</span>
																							<CheckCircleIcon
																								color="success"
																								fontSize="small"
																							/>
																						</>
																					) : (
																						<>
																							<span>→</span>
																							<CancelIcon
																								color="error"
																								fontSize="small"
																							/>
																						</>
																					)
																				) : (
																					<></>
																				)}
																			</Stack>
																		</TableCell>
																		<TableCell width={50}>
																			<Button
																				size="small"
																				onClick={() =>
																					handleRowClick(
																						productType.productType
																							.productTypeID,
																						"productType"
																					)
																				}
																				disabled={filteredProducts.length === 0}
																			>
																				{isProductTypeExpanded ? (
																					<KeyboardArrowDownIcon />
																				) : (
																					<KeyboardArrowRightIcon />
																				)}
																			</Button>
																		</TableCell>
																		<TableCell width={500}>
																			<strong>
																				{getProductTypeDisplayName(
																					productType.productType.productTypeID
																				) ?? productType.productType.name}
																			</strong>
																		</TableCell>
																		<TableCell>
																			{productType.productType.description}
																		</TableCell>
																		<TableCell width={40}>
																			{!!productTypeMessage && (
																				<Button
																					color="error"
																					onClick={() =>
																						handleRemoveItem(
																							productType.productType
																								.productTypeID,
																							"productTypes"
																						)
																					}
																					size="small"
																					sx={{
																						height: 24,
																					}}
																				>
																					Zrušit
																				</Button>
																			)}
																		</TableCell>
																	</TableRow>
																	{/* Render products in a separate table */}
																	{isProductTypeExpanded &&
																		filteredProducts.length > 0 && (
																			<TableRow>
																				<TableCell
																					colSpan={5}
																					style={{ padding: 0 }}
																				>
																					<Table size="small">
																						{/* <TableHead>
																						<TableRow>
																							<TableCell width="9%">
																								<strong>Produkt</strong>
																							</TableCell>
																							<TableCell width="5%"></TableCell>
																							<TableCell>
																								<strong>Product Name</strong>
																							</TableCell>
																							<TableCell>
																								<strong>Institution</strong>
																							</TableCell>
																						</TableRow>
																					</TableHead> */}
																						<TableBody>
																							{filteredProducts.map(
																								(product) => {
																									const productKey = `product-${product.product.productID}`;
																									const isProductExpanded =
																										expandedRows.has(
																											productKey
																										);

																									// Filter tariffs under this product
																									const filteredTariffs =
																										productDeviceModel.tariffs.filter(
																											(tariff) =>
																												tariff.tariff
																													.productID ===
																												product.product
																													.productID
																										);

																									const productMessage = (
																										productEnableDisableMessage.products ||
																										[]
																									).find(
																										(p) =>
																											p.id ===
																											product.product.productID
																									);

																									return (
																										<>
																											{/* Product Row */}
																											<TableRow
																												key={productKey}
																												sx={{
																													backgroundColor:
																														!!productMessage &&
																														productMessage.doEnable !==
																															undefined
																															? productMessage.doEnable
																																? "rgb(250, 255, 248)"
																																: "rgb(255, 248, 248)"
																															: "",
																												}}
																											>
																												<TableCell width={250}>
																													<Stack
																														direction="row"
																														spacing={1}
																														alignItems="center"
																													>
																														<Checkbox
																															checked={
																																filteredTariffs.length >
																																	0 &&
																																filteredTariffs.every(
																																	(tariff) =>
																																		selectionModel.tariffs.includes(
																																			tariff
																																				.tariff
																																				.tariffID
																																		)
																																)
																															}
																															indeterminate={
																																filteredTariffs.some(
																																	(tariff) =>
																																		selectionModel.tariffs.includes(
																																			tariff
																																				.tariff
																																				.tariffID
																																		)
																																) &&
																																!filteredTariffs.every(
																																	(tariff) =>
																																		selectionModel.tariffs.includes(
																																			tariff
																																				.tariff
																																				.tariffID
																																		)
																																)
																															}
																															onChange={() =>
																																handleSelectAll(
																																	"tariffs",
																																	filteredTariffs.map(
																																		(tariff) =>
																																			tariff
																																				.tariff
																																				.tariffID
																																	)
																																)
																															}
																															title="Vybrat všechny potomky"
																															sx={{ p: 0.1 }}
																															icon={
																																<FactCheckOutlinedIcon />
																															}
																															indeterminateIcon={
																																<FactCheckCircleIcon />
																															}
																															checkedIcon={
																																<FactCheckCircleIcon />
																															}
																														/>
																														<Checkbox
																															checked={selectionModel.products.includes(
																																product.product
																																	.productID
																															)}
																															onChange={() =>
																																handleSelectionChange(
																																	product
																																		.product
																																		.productID,
																																	"products"
																																)
																															}
																															title="Vybrat"
																															sx={{ p: 0.1 }}
																														/>
																														{deviceForm.devices
																															.length === 1 ? (
																															product.product
																																.isActive ? (
																																<CheckCircleIcon
																																	color="success"
																																	fontSize="small"
																																/>
																															) : (
																																<CancelIcon
																																	color="error"
																																	fontSize="small"
																																/>
																															)
																														) : (
																															<QuestionMarkIcon
																																color="disabled"
																																fontSize="small"
																															/>
																														)}
																														{!!productMessage &&
																														productMessage.doEnable !==
																															undefined ? (
																															productMessage.doEnable ? (
																																<>
																																	<span>→</span>
																																	<CheckCircleIcon
																																		color="success"
																																		fontSize="small"
																																	/>
																																</>
																															) : (
																																<>
																																	<span>→</span>
																																	<CancelIcon
																																		color="error"
																																		fontSize="small"
																																	/>
																																</>
																															)
																														) : (
																															<></>
																														)}
																													</Stack>
																												</TableCell>
																												<TableCell width={50}>
																													<Button
																														size="small"
																														onClick={() =>
																															handleRowClick(
																																product.product
																																	.productID,
																																"product"
																															)
																														}
																													>
																														{isProductExpanded ? (
																															<KeyboardArrowDownIcon />
																														) : (
																															<KeyboardArrowRightIcon />
																														)}
																													</Button>
																												</TableCell>
																												<TableCell width={500}>
																													<strong>
																														{
																															product.product
																																.name
																														}
																													</strong>
																												</TableCell>
																												<TableCell width={400}>
																													{getInstitutionLabel(
																														product.product
																															.institutionID
																													)}
																												</TableCell>
																												<TableCell>
																													<Stack
																														direction="row"
																														spacing={1}
																														alignItems="center"
																													>
																														{deviceForm.devices
																															.length === 1 ? (
																															<BlTooltip
																																placement="right"
																																title={
																																	product
																																		.product
																																		.isContractCreateEnabled
																																		? "Sjednání povoleno"
																																		: "Sjednání zakázáno"
																																}
																															>
																																<HistoryEduIcon
																																	color={
																																		product
																																			.product
																																			.isContractCreateEnabled
																																			? "success"
																																			: "error"
																																	}
																																/>
																															</BlTooltip>
																														) : (
																															<QuestionMarkIcon
																																color="disabled"
																																fontSize="small"
																															/>
																														)}
																														{!!productMessage &&
																														productMessage.doEnableContractCreate !==
																															undefined &&
																														productMessage.doEnableContractCreate !==
																															null ? (
																															<>
																																<span>→</span>
																																<BlTooltip
																																	placement="right"
																																	title={
																																		productMessage.doEnableContractCreate
																																			? "Sjednání povoleno"
																																			: "Sjednání zakázáno"
																																	}
																																>
																																	<HistoryEduIcon
																																		color={
																																			productMessage.doEnableContractCreate
																																				? "success"
																																				: "error"
																																		}
																																	/>
																																</BlTooltip>
																															</>
																														) : (
																															<></>
																														)}
																													</Stack>
																												</TableCell>
																												<TableCell width={40}>
																													{!!productMessage && (
																														<Button
																															color="error"
																															onClick={() =>
																																handleRemoveItem(
																																	product
																																		.product
																																		.productID,
																																	"products"
																																)
																															}
																															size="small"
																															sx={{
																																height: 24,
																															}}
																														>
																															Zrušit
																														</Button>
																													)}
																												</TableCell>
																											</TableRow>
																											{/* Render tariffs */}
																											{isProductExpanded && (
																												<TableRow>
																													<TableCell
																														colSpan={6}
																														style={{
																															padding: 0,
																														}}
																													>
																														<Table size="small">
																															{/* <TableHead>
																														<TableRow>
																															<TableCell width="11%">
																																<strong>
																																	Tarif
																																</strong>
																															</TableCell>
																															<TableCell>
																																<strong>
																																	Tariff Name
																																</strong>
																						
																																</TableCell>
																															<TableCell>
																																<strong>
																																	Description
																																</strong>
																															</TableCell>
																														</TableRow>
																													</TableHead> */}
																															<TableBody>
																																{filteredTariffs.map(
																																	(tariff) => {
																																		const tariffMessage =
																																			(
																																				productEnableDisableMessage.tariffs ||
																																				[]
																																			).find(
																																				(t) =>
																																					t.id ===
																																					tariff
																																						.tariff
																																						.tariffID
																																			);

																																		return (
																																			<TableRow
																																				key={`tariff-${tariff.tariff.tariffID}`}
																																				sx={{
																																					backgroundColor:
																																						!!tariffMessage
																																							? tariffMessage.doEnable
																																								? "rgb(250, 255, 248)"
																																								: "rgb(255, 248, 248)"
																																							: "",
																																				}}
																																			>
																																				<TableCell
																																					width={
																																						400
																																					}
																																				>
																																					<Stack
																																						direction="row"
																																						spacing={
																																							1
																																						}
																																						alignItems="center"
																																						sx={{
																																							ml: 3.6,
																																						}}
																																					>
																																						<Checkbox
																																							checked={selectionModel.tariffs.includes(
																																								tariff
																																									.tariff
																																									.tariffID
																																							)}
																																							onChange={() =>
																																								handleSelectionChange(
																																									tariff
																																										.tariff
																																										.tariffID,
																																									"tariffs"
																																								)
																																							}
																																							sx={{
																																								p: 0.1,
																																								ml: 3.6,
																																							}}
																																						/>
																																						<Stack
																																							direction="row"
																																							spacing={
																																								1
																																							}
																																						>
																																							{deviceForm
																																								.devices
																																								.length ===
																																							1 ? (
																																								tariff
																																									.tariff
																																									.isActive ? (
																																									<CheckCircleIcon
																																										color="success"
																																										fontSize="small"
																																									/>
																																								) : (
																																									<CancelIcon
																																										color="error"
																																										fontSize="small"
																																									/>
																																								)
																																							) : (
																																								<QuestionMarkIcon
																																									color="disabled"
																																									fontSize="small"
																																								/>
																																							)}
																																							{!!tariffMessage ? (
																																								tariffMessage.doEnable ? (
																																									<>
																																										<span>
																																											→
																																										</span>
																																										<CheckCircleIcon
																																											color="success"
																																											fontSize="small"
																																										/>
																																									</>
																																								) : (
																																									<>
																																										<span>
																																											→
																																										</span>
																																										<CancelIcon
																																											color="error"
																																											fontSize="small"
																																										/>
																																									</>
																																								)
																																							) : (
																																								<>

																																								</>
																																							)}
																																						</Stack>
																																					</Stack>
																																				</TableCell>
																																				<TableCell
																																					width={
																																						500
																																					}
																																				>
																																					{
																																						tariff
																																							.tariff
																																							.name
																																					}
																																				</TableCell>
																																				<TableCell>
																																					{
																																						tariff
																																							.tariff
																																							.description
																																					}
																																				</TableCell>
																																				<TableCell
																																					width={
																																						40
																																					}
																																				>
																																					{!!tariffMessage && (
																																						<Button
																																							color="error"
																																							onClick={() =>
																																								handleRemoveItem(
																																									tariff
																																										.tariff
																																										.tariffID,
																																									"tariffs"
																																								)
																																							}
																																							size="small"
																																							sx={{
																																								height: 24,
																																							}}
																																						>
																																							Zrušit
																																						</Button>
																																					)}
																																				</TableCell>
																																			</TableRow>
																																		);
																																	}
																																)}
																															</TableBody>
																														</Table>
																													</TableCell>
																												</TableRow>
																											)}
																										</>
																									);
																								}
																							)}
																						</TableBody>
																					</Table>
																				</TableCell>
																			</TableRow>
																		)}
																</>
															);
														})}
													</TableBody>
												</Table>
											</TableCell>
										</TableRow>
									)}
								</>
							);
						})}
					</TableBody>
				</Table>
			</TableContainer>
		</Stack>
	);
};

export default ProductListTable;
